import React, { useState, useEffect } from 'react';
import { Shield, Settings, Save, Eye, EyeOff, Users, Search, Plus, Calendar, Target, Trophy, ArrowLeft, ChevronLeft, ChevronRight, MapPin, X, Edit, Check, Home, ChevronDown, Trash2 } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { collection, query, getDocs, orderBy, addDoc, serverTimestamp, where, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { useStore } from '../store';
import type { Profile, Game, ShotType, GameSituation } from '../types';
import GoalDiagram from '../components/GoalDiagram';
import ShotPositionDiagram from '../components/ShotPositionDiagram';

interface AdminSettings {
  freeGamesLimit: number;
  freeAnalysisLimit: number;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  prices: {
    id: string;
    amount: number;
    currency: string;
    nickname: string;
  }[];
}

const shotTypeOptions: ShotType[] = [
  'Wrist Shot',
  'Slap Shot',
  'Snap Shot',
  'Backhand',
  'Deflection / Tip-in',
  'One-Timer',
  'Wraparound',
  'Rebound Shot'
];

const gameSituationOptions: GameSituation[] = [
  'Break away',
  'Penalty Shot',
  'Screened Shot',
  'Odd-Man Rush',
  'Cross-Ice Pass',
  'Point Shot',
  'Net-Front Battle',
  'Shorthanded Goal',
  'Power Play Goal',
  'Turnover Leading to Goal',
  'Off the Rush',
  'Puck Battle / Scramble'
];

export default function AdminPanel() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const profile = useStore((state) => state.profile);
  const [isAdminAuthorized, setIsAdminAuthorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Settings form
  const [settings, setSettings] = useState<AdminSettings>({
    freeGamesLimit: 1,
    freeAnalysisLimit: 1,
  });

  // Stripe products
  const [stripeProducts, setStripeProducts] = useState<StripeProduct[]>([]);
  const [productPrices, setProductPrices] = useState<{ [key: string]: number }>(
    {}
  );

  // Goalie management
  const [goalies, setGoalies] = useState<Profile[]>([]);
  const [selectedGoalie, setSelectedGoalie] = useState<Profile | null>(null);
  const [goalieSearch, setGoalieSearch] = useState('');
  const [goaliesLoading, setGoaliesLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalGoalies, setTotalGoalies] = useState(0);
  const goaliesPerPage = 5;

  // Game creation
  const [showGameForm, setShowGameForm] = useState(false);
  const [gameDate, setGameDate] = useState('');
  const [opponent, setOpponent] = useState('');
  const [competitionType, setCompetitionType] = useState<'regular-season' | 'playoff' | 'tournament' | 'exhibition' | 'practice'>('regular-season');
  const [shotsFaced, setShotsFaced] = useState('');
  const [saves, setSaves] = useState('');
  const [outcome, setOutcome] = useState<'win' | 'loss' | 'win-ot' | 'loss-ot' | 'win-so' | 'loss-so' | null>(null);

  // Game management
  const [goalieGames, setGoalieGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [showGoalManagement, setShowGoalManagement] = useState(false);
  
  // Games pagination
  const [gamesCurrentPage, setGamesCurrentPage] = useState(1);
  const [totalGames, setTotalGames] = useState(0);
  const gamesPerPage = 5;

  // Goal creation for admin
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [goalResult, setGoalResult] = useState<'save' | 'goal'>('goal');
  const [shotType, setShotType] = useState<ShotType>(null);
  const [gameSituation, setGameSituation] = useState<GameSituation>(null);
  const [xPosition, setXPosition] = useState<number>(50);
  const [yPosition, setYPosition] = useState<number>(50);
  const [shotXPosition, setShotXPosition] = useState<number | null>(null);
  const [shotYPosition, setShotYPosition] = useState<number | null>(null);
  
  // Workflow steps: 'goalies' -> 'games' -> 'goals' -> 'edit-goal' | 'edit-game'
  const [currentStep, setCurrentStep] = useState<'goalies' | 'games' | 'goals' | 'edit-goal' | 'edit-game' | 'add-goal' | 'add-game'>('goalies');
  
  // Game details view
  const [showGameDetails, setShowGameDetails] = useState(false);
  const [gameGoals, setGameGoals] = useState<any[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  
  // Goals pagination
  const [currentGoalsPage, setCurrentGoalsPage] = useState(1);
  const goalsPerPage = 5;
  
  // Goal editing
  const [editingGoal, setEditingGoal] = useState<any | null>(null);
  const [editGoalTitle, setEditGoalTitle] = useState('');
  const [editGoalDescription, setEditGoalDescription] = useState('');
  const [editGoalResult, setEditGoalResult] = useState<'save' | 'goal'>('goal');
  const [editShotType, setEditShotType] = useState<ShotType>(null);
  const [editGameSituation, setEditGameSituation] = useState<GameSituation>(null);
  const [editXPosition, setEditXPosition] = useState<number>(50);
  const [editYPosition, setEditYPosition] = useState<number>(50);
  const [editShotXPosition, setEditShotXPosition] = useState<number | null>(null);
  const [editShotYPosition, setEditShotYPosition] = useState<number | null>(null);
  
  // Game deletion
  const [gameToDelete, setGameToDelete] = useState<Game | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Goal deletion
  const [goalToDelete, setGoalToDelete] = useState<any | null>(null);
  const [showGoalDeleteConfirmation, setShowGoalDeleteConfirmation] = useState(false);

  // Check if user has admin privileges
  useEffect(() => {
    if (user && profile) {
      const isAuthorized = profile.level?.toLowerCase() === 'recreational';
      console.log('Checking admin authorization:', { level: profile.level, isAuthorized });
      setIsAdminAuthorized(isAuthorized);
      
      if (isAuthorized && !goaliesLoading) {
        console.log('Loading goalies for authorized admin...');
        fetchGoalies(1, '');
        console.log('Loading Stripe products...');
        fetchStripeProducts();
      }
    } else {
      setIsAdminAuthorized(false);
    }
  }, [user, profile]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('Attempting Firebase auth login...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      console.log('Firebase auth successful, checking profile...');
      // The user and profile will be set by the store automatically
      // We'll check authorization in the useEffect
      
      setPassword(''); // Clear password
      setEmail(''); // Clear email
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('User not found. Please check your email.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(err.message || t('adminPanel.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_ADMIN_URL}/settings`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': import.meta.env.VITE_API_KEY,
          },
          body: JSON.stringify({
            freeGamesLimit: settings.freeGamesLimit,
            freeAnalysisLimit: settings.freeAnalysisLimit,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('adminPanel.updateFailed'));
      }

      setSuccess(t('adminPanel.settingsUpdatedSuccessfully'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminPanel.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeProducts = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_ADMIN_URL}/stripe-products`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': import.meta.env.VITE_API_KEY,
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('adminPanel.failedToFetchProducts'));
      }

      setStripeProducts(data.products);

      // Initialize product prices state with latest price
      const prices: { [key: string]: number } = {};
      data.products.forEach((product: StripeProduct) => {
        if (product.prices.length > 0) {
          // Use the first price (latest due to backend sorting)
          prices[product.id] = (product.prices[0].amount || 0) / 100; // Convert from cents
        }
      });
      setProductPrices(prices);
    } catch (err) {
      console.error('Error fetching Stripe products:', err);
      setError(t('adminPanel.failedToFetchProducts'));
    }
  };

  const handleUpdatePrice = async (productId: string) => {
    const newAmount = productPrices[productId];
    if (!newAmount || newAmount <= 0) {
      setError(t('adminPanel.pleaseEnterValidPrice'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_ADMIN_URL}/update-price`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': import.meta.env.VITE_API_KEY,
          },
          body: JSON.stringify({
            productId,
            newAmount,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || t('adminPanel.failedToUpdatePrice'));
      }

      setSuccess(`${t('adminPanel.priceUpdatedSuccessfully')} ${productId}`);
      setTimeout(() => setSuccess(''), 3000);

      // Refresh products
      await fetchStripeProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminPanel.failedToUpdatePrice'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAdminAuthorized(false);
      setEmail('');
      setPassword('');
      setError('');
      setSuccess('');
      setSelectedGoalie(null);
      setGoalies([]);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const fetchGoalies = async (page = 1, searchTerm = '') => {
    try {
      setGoaliesLoading(true);
      
      console.log('Fetching goalies, page:', page, 'searchTerm:', searchTerm);
      
      // First, get all goalies to calculate total count for pagination
      const allGoaliesQuery = query(
        collection(db, 'profiles')
      );
      const allSnapshot = await getDocs(allGoaliesQuery);
      
      console.log('Total profiles found:', allSnapshot.docs.length);
      
      // Filter by search term if provided
      let filteredGoalies = allSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt) : new Date()
        };
      }) as Profile[];

      // Sort by createdAt desc
      filteredGoalies.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      if (searchTerm) {
        filteredGoalies = filteredGoalies.filter(goalie =>
          `${goalie.firstName} ${goalie.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          goalie.team?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      console.log('Filtered goalies:', filteredGoalies.length);

      // Calculate pagination
      const startIndex = (page - 1) * goaliesPerPage;
      const endIndex = startIndex + goaliesPerPage;
      const paginatedGoalies = filteredGoalies.slice(startIndex, endIndex);
      
      console.log('Paginated goalies for page', page, ':', paginatedGoalies.length);
      
      setTotalGoalies(filteredGoalies.length);
      setGoalies(paginatedGoalies);
    } catch (err) {
      console.error('Error fetching goalies:', err);
      if (err.code === 'permission-denied') {
        setError('Admin does not have permission to read profiles. Please check Firestore security rules.');
      } else {
        setError(`Failed to fetch goalies: ${err.message}`);
      }
    } finally {
      setGoaliesLoading(false);
    }
  };

  const fetchGoalieGames = async (goalieId: string, page = 1) => {
    try {
      setGamesLoading(true);
      console.log('Fetching games for goalie:', goalieId, 'page:', page);
      
      const gamesQuery = query(
        collection(db, 'games'),
        where('userId', '==', goalieId)
      );
      const snapshot = await getDocs(gamesQuery);
      
      console.log('Games found:', snapshot.docs.length);
      
      const allGames = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          date: data.date ? new Date(data.date) : new Date(),
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date()
        };
      }) as Game[];
      
      // Sort by date desc manually
      allGames.sort((a, b) => b.date.getTime() - a.date.getTime());
      
      // Apply pagination
      const startIndex = (page - 1) * gamesPerPage;
      const endIndex = startIndex + gamesPerPage;
      const paginatedGames = allGames.slice(startIndex, endIndex);
      
      console.log('Paginated games for page', page, ':', paginatedGames.length);
      setTotalGames(allGames.length);
      setGoalieGames(paginatedGames);
    } catch (err) {
      console.error('Error fetching goalie games:', err);
      setError('Failed to fetch games');
    } finally {
      setGamesLoading(false);
    }
  };

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGoalie) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!gameDate) {
        setError('Please select a game date');
        setLoading(false);
        return;
      }
      
      if (!opponent || opponent.trim() === '') {
        setError('Please enter the opponent team name');
        setLoading(false);
        return;
      }
      
      const selectedDate = new Date(gameDate);
      
      const gameData = {
        userId: selectedGoalie.id,
        date: selectedDate.getTime(),
        opponent: opponent.trim(),
        competitionType,
        shotsFaced: shotsFaced ? parseInt(shotsFaced, 10) : null,
        saves: saves ? parseInt(saves, 10) : null,
        outcome,
        createdAt: Date.now(),
      };
      
      console.log('Creating game with data:', gameData);

      await addDoc(collection(db, 'games'), gameData);
      
      setSuccess(`Game created successfully for ${selectedGoalie.firstName} ${selectedGoalie.lastName}`);
      setTimeout(() => setSuccess(''), 3000);
      
      // Reset form and return to games list
      setCurrentStep('games');
      setShowGameForm(false);
      setGameDate('');
      setOpponent('');
      setCompetitionType('regular-season');
      setShotsFaced('');
      setSaves('');
      setOutcome(null);
      
      // Refresh games list
      fetchGoalieGames(selectedGoalie.id, gamesCurrentPage);
    } catch (err) {
      console.error('Error creating game:', err);
      // Provide more detailed error message
      let errorMessage = 'Failed to create game';
      if (err instanceof Error) {
        errorMessage += `: ${err.message}`;
        // Check for common Firebase errors
        if (err.message.includes('Missing or insufficient permissions')) {
          errorMessage = 'Permission denied. Please ensure the user has proper permissions to create games.';
        } else if (err.message.includes('document')) {
          errorMessage = 'Invalid game data. Please check all fields and try again.';
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchGameGoals = async (gameId: string) => {
    try {
      setGoalsLoading(true);
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', gameId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(goalsQuery);
      
      const goalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt ? new Date(doc.data().createdAt) : new Date()
      }));
      
      setGameGoals(goalsData);
    } catch (err) {
      console.error('Error fetching goals:', err);
      // Try without orderBy if it fails
      try {
        const goalsQuery = query(
          collection(db, 'goals'),
          where('gameId', '==', gameId)
        );
        const snapshot = await getDocs(goalsQuery);
        
        const goalsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt ? new Date(doc.data().createdAt) : new Date()
        }));
        
        // Sort manually
        goalsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        
        setGameGoals(goalsData);
      } catch (err2) {
        console.error('Error fetching goals (second attempt):', err2);
        setError('Failed to fetch goals');
      }
    } finally {
      setGoalsLoading(false);
    }
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGame || !selectedGoalie) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const goalData = {
        gameId: selectedGame.id,
        userId: selectedGoalie.id,
        title: goalTitle,
        xPosition: xPosition,
        yPosition: yPosition,
        shotXPosition: shotXPosition,
        shotYPosition: shotYPosition,
        situationDescription: goalDescription,
        shotType: shotType,
        gameSituation: gameSituation,
        result: goalResult,
        status: 'completed' as const,
        createdAt: serverTimestamp(),
        isAIGenerated: false
      };

      // Remove undefined fields to avoid Firestore errors
      Object.keys(goalData).forEach(key => {
        if (goalData[key as keyof typeof goalData] === undefined) {
          delete goalData[key as keyof typeof goalData];
        }
      });

      await addDoc(collection(db, 'goals'), goalData);
      
      setSuccess(`Goal created successfully for ${selectedGoalie.firstName} ${selectedGoalie.lastName}`);
      setTimeout(() => setSuccess(''), 3000);
      
      // Reset form and return to goals list
      setCurrentStep('goals');
      setShowGoalForm(false);
      setGoalTitle('');
      setGoalDescription('');
      setGoalResult('goal');
      setShotType(null);
      setGameSituation(null);
      setXPosition(50);
      setYPosition(50);
      setShotXPosition(null);
      setShotYPosition(null);
      
      // Refresh goals list
      fetchGameGoals(selectedGame.id);
    } catch (err) {
      console.error('Error creating goal:', err);
      setError('Failed to create goal');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGoal = (goal: any) => {
    setCurrentStep('edit-goal');
    setEditingGoal(goal);
    setEditGoalTitle(goal.title || '');
    setEditGoalDescription(goal.situationDescription || '');
    setEditGoalResult(goal.result || 'goal');
    setEditShotType(goal.shotType || null);
    setEditGameSituation(goal.gameSituation || null);
    setEditXPosition(goal.xPosition || 50);
    setEditYPosition(goal.yPosition || 50);
    setEditShotXPosition(goal.shotXPosition || null);
    setEditShotYPosition(goal.shotYPosition || null);
  };

  const handleUpdateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGoal || !selectedGame) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const goalData = {
        title: editGoalTitle,
        xPosition: editXPosition,
        yPosition: editYPosition,
        shotXPosition: editShotXPosition,
        shotYPosition: editShotYPosition,
        situationDescription: editGoalDescription,
        shotType: editShotType,
        gameSituation: editGameSituation,
        result: editGoalResult,
      };

      // Remove undefined fields to avoid Firestore errors
      Object.keys(goalData).forEach(key => {
        if (goalData[key as keyof typeof goalData] === undefined) {
          delete goalData[key as keyof typeof goalData];
        }
      });

      await updateDoc(doc(db, 'goals', editingGoal.id), goalData);
      
      setSuccess(`Goal updated successfully`);
      setTimeout(() => setSuccess(''), 3000);
      
      // Reset editing state and return to goals list
      setEditingGoal(null);
      setCurrentStep('goals');
      setEditGoalTitle('');
      setEditGoalDescription('');
      setEditGoalResult('goal');
      setEditShotType(null);
      setEditGameSituation(null);
      setEditXPosition(50);
      setEditYPosition(50);
      setEditShotXPosition(null);
      setEditShotYPosition(null);
      
      // Refresh goals list
      fetchGameGoals(selectedGame.id);
    } catch (err) {
      console.error('Error updating goal:', err);
      setError('Failed to update goal');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingGoal(null);
    setCurrentStep('goals');
    setEditGoalTitle('');
    setEditGoalDescription('');
    setEditGoalResult('goal');
    setEditShotType(null);
    setEditGameSituation(null);
    setEditXPosition(50);
    setEditYPosition(50);
    setEditShotXPosition(null);
    setEditShotYPosition(null);
  };

  // Handle search with debouncing
  const handleSearch = (searchTerm: string) => {
    setGoalieSearch(searchTerm);
    setCurrentPage(1); // Reset to first page when searching
    fetchGoalies(1, searchTerm);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchGoalies(newPage, goalieSearch);
  };

  // Handle games page change
  const handleGamesPageChange = (newPage: number) => {
    setGamesCurrentPage(newPage);
    if (selectedGoalie) {
      fetchGoalieGames(selectedGoalie.id, newPage);
    }
  };

  // Calculate goalies pagination info
  const totalPages = Math.ceil(totalGoalies / goaliesPerPage);
  const startIndex = (currentPage - 1) * goaliesPerPage + 1;
  const endIndex = Math.min(currentPage * goaliesPerPage, totalGoalies);
  
  // Calculate games pagination info
  const gamesTotalPages = Math.ceil(totalGames / gamesPerPage);
  const gamesStartIndex = (gamesCurrentPage - 1) * gamesPerPage + 1;
  const gamesEndIndex = Math.min(gamesCurrentPage * gamesPerPage, totalGames);

  // Calculate goals pagination info
  const totalGoals = gameGoals.length;
  const goalsTotalPages = Math.ceil(totalGoals / goalsPerPage);
  const goalsStartIndex = (currentGoalsPage - 1) * goalsPerPage;
  const goalsEndIndex = Math.min(currentGoalsPage * goalsPerPage, totalGoals);
  const paginatedGoals = gameGoals.slice(goalsStartIndex, goalsEndIndex);

  // Workflow navigation functions
  const navigateToGoalies = () => {
    setCurrentStep('goalies');
    setSelectedGoalie(null);
    setSelectedGame(null);
    setEditingGoal(null);
    setShowGameForm(false);
    setShowGoalForm(false);
    setShowGoalManagement(false);
    setShowGameDetails(false);
    setCurrentGoalsPage(1);
  };

  const navigateToGames = () => {
    setCurrentStep('games');
    setSelectedGame(null);
    setEditingGoal(null);
    setShowGameForm(false);
    setShowGoalForm(false);
    setShowGameDetails(false);
    if (selectedGoalie) {
      fetchGoalieGames(selectedGoalie.id, 1);
    }
  };

  const navigateToGoals = () => {
    setCurrentStep('goals');
    setEditingGoal(null);
    setShowGoalForm(false);
    if (selectedGame) {
      fetchGameGoals(selectedGame.id);
    }
  };

  const handleGoalieSelect = (goalie: Profile) => {
    setSelectedGoalie(goalie);
    setCurrentStep('games');
    fetchGoalieGames(goalie.id, 1);
  };

  const handleGameSelect = (game: Game) => {
    setSelectedGame(game);
    setCurrentStep('goals');
    setCurrentGoalsPage(1);
    fetchGameGoals(game.id);
  };

  const handleGoalEdit = (goal: any) => {
    setCurrentStep('edit-goal');
    setEditingGoal(goal);
    setEditGoalTitle(goal.title || '');
    setEditGoalDescription(goal.situationDescription || '');
    setEditGoalResult(goal.result || 'goal');
    setEditShotType(goal.shotType || null);
    setEditGameSituation(goal.gameSituation || null);
    setEditXPosition(goal.xPosition || 50);
    setEditYPosition(goal.yPosition || 50);
    setEditShotXPosition(goal.shotXPosition || null);
    setEditShotYPosition(goal.shotYPosition || null);
  };

  const handleAddGame = () => {
    setCurrentStep('add-game');
    setShowGameForm(true);
  };

  const handleAddGoal = () => {
    setCurrentStep('add-goal');
    setShowGoalForm(true);
  };

  const handleDeleteGameClick = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent game selection when clicking delete
    setGameToDelete(game);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteGame = async () => {
    if (!gameToDelete || !selectedGoalie) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Delete the game document
      await deleteDoc(doc(db, 'games', gameToDelete.id));

      // Also delete all associated goals
      const goalsQuery = query(
        collection(db, 'goals'),
        where('gameId', '==', gameToDelete.id)
      );
      const goalsSnapshot = await getDocs(goalsQuery);
      
      // Delete all goals for this game
      await Promise.all(
        goalsSnapshot.docs.map(goalDoc => deleteDoc(goalDoc.ref))
      );

      setSuccess(`Game vs ${gameToDelete.opponent} and its associated goals deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);

      // Reset state
      setShowDeleteConfirmation(false);
      setGameToDelete(null);
      
      // If the deleted game was selected, clear selection
      if (selectedGame?.id === gameToDelete.id) {
        setSelectedGame(null);
        setCurrentStep('games');
        setGameGoals([]);
      }

      // Refresh games list
      fetchGoalieGames(selectedGoalie.id, gamesCurrentPage);
    } catch (err) {
      console.error('Error deleting game:', err);
      setError('Failed to delete game');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setGameToDelete(null);
  };

  const handleDeleteGoalClick = (goal: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent goal editing when clicking delete
    setGoalToDelete(goal);
    setShowGoalDeleteConfirmation(true);
  };

  const handleDeleteGoal = async () => {
    if (!goalToDelete || !selectedGame) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Delete the goal document
      await deleteDoc(doc(db, 'goals', goalToDelete.id));

      setSuccess(`Goal "${goalToDelete.title}" deleted successfully`);
      setTimeout(() => setSuccess(''), 3000);

      // Reset state
      setShowGoalDeleteConfirmation(false);
      setGoalToDelete(null);
      
      // If the deleted goal was being edited, clear editing state
      if (editingGoal?.id === goalToDelete.id) {
        setEditingGoal(null);
        setCurrentStep('goals');
        setEditGoalTitle('');
        setEditGoalDescription('');
        setEditGoalResult('goal');
        setEditShotType(null);
        setEditGameSituation(null);
        setEditXPosition(50);
        setEditYPosition(50);
        setEditShotXPosition(null);
        setEditShotYPosition(null);
      }

      // Refresh goals list
      fetchGameGoals(selectedGame.id);
    } catch (err) {
      console.error('Error deleting goal:', err);
      setError('Failed to delete goal');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGoalDelete = () => {
    setShowGoalDeleteConfirmation(false);
    setGoalToDelete(null);
  };

  // Show login form if not authenticated or not authorized
  if (!user || !isAdminAuthorized) {
    return (
      <div className='min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full bg-white rounded-lg shadow-md p-8'>
          <div className='text-center mb-8'>
            <Shield className='h-12 w-12 text-[#1b3968] mx-auto mb-4' />
            <h2 className='text-2xl font-bold text-gray-900'>{t('adminPanel.title')}</h2>
            <p className='text-gray-600'>
              {user && !isAdminAuthorized 
                ? 'Access denied. Only users with "Recreational" level can access admin panel.' 
                : t('adminPanel.signInToManage')}
            </p>
          </div>

          {error && (
            <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
              {error}
            </div>
          )}

          {!user && (
            <form onSubmit={handleLogin} className='space-y-6'>
              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  required
                  disabled={loading}
                />
              </div>

            <div>
              <label
                htmlFor='password'
                className='block text-sm font-medium text-gray-700 mb-2'
              >
                {t('adminPanel.password')}
              </label>
              <div className='relative'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  required
                  disabled={loading}
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute inset-y-0 right-0 pr-3 flex items-center'
                >
                  {showPassword ? (
                    <EyeOff className='h-5 w-5 text-gray-400' />
                  ) : (
                    <Eye className='h-5 w-5 text-gray-400' />
                  )}
                </button>
              </div>
            </div>

              <button
                type='submit'
                disabled={loading}
                className='w-full bg-[#1b3968] text-white py-2 px-4 rounded-md hover:bg-[#1b3968]/90 focus:outline-none focus:ring-2 focus:ring-[#1b3968] disabled:opacity-50'
              >
                {loading ? t('adminPanel.signingIn') : t('adminPanel.signIn')}
              </button>
            </form>
          )}

          {user && !isAdminAuthorized && (
            <div className='text-center'>
              <p className='text-sm text-gray-600 mb-4'>
                Current user level: <strong>{profile?.level || 'Unknown'}</strong>
              </p>
              <button
                onClick={handleLogout}
                className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm font-medium'
              >
                Sign out and try with a different account
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-2xl mx-auto'>
        <div className='bg-white rounded-lg shadow-md p-8'>
          <div className='flex justify-between items-center mb-8'>
            <div className='flex items-center'>
              <Settings className='h-8 w-8 text-[#1b3968] mr-3' />
              <h1 className='text-2xl font-bold text-primary-600 italic'>
                {t('adminPanel.systemSettings')}
              </h1>
            </div>
            <button
              onClick={handleLogout}
              className='text-gray-600 hover:text-gray-900 text-sm'
            >
              {t('adminPanel.logout')}
            </button>
          </div>

          {error && (
            <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
              {error}
            </div>
          )}

          {success && (
            <div className='bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6'>
              {success}
            </div>
          )}

          <form onSubmit={handleUpdateSettings} className='space-y-6'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label
                  htmlFor='freeGamesLimit'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  {t('adminPanel.freeGamesLimit')}
                </label>
                <input
                  type='number'
                  id='freeGamesLimit'
                  min='0'
                  value={settings.freeGamesLimit}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      freeGamesLimit: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  required
                  disabled={loading}
                />
                <p className='text-sm text-gray-500 mt-1'>
                  {t('adminPanel.freeGamesDescription')}
                </p>
              </div>

              <div>
                <label
                  htmlFor='freeAnalysisLimit'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  {t('adminPanel.freeAIAnalysisLimit')}
                </label>
                <input
                  type='number'
                  id='freeAnalysisLimit'
                  min='0'
                  value={settings.freeAnalysisLimit}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      freeAnalysisLimit: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  required
                  disabled={loading}
                />
                <p className='text-sm text-gray-500 mt-1'>
                  {t('adminPanel.freeAnalysisDescription')}
                </p>
              </div>
            </div>

            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={loading}
                className='bg-[#1b3968] text-white px-6 py-2 rounded-md hover:bg-[#1b3968]/90 focus:outline-none focus:ring-2 focus:ring-[#1b3968] disabled:opacity-50 flex items-center gap-2'
              >
                <Save className='h-5 w-5' />
                {loading ? t('adminPanel.saving') : t('adminPanel.saveSettings')}
              </button>
            </div>
          </form>
        </div>

        {/* Stripe Products Section */}
        <div className='bg-white rounded-lg shadow-md p-8 mt-8'>
          <h2 className='text-xl font-bold text-gray-900 mb-6'>
            {t('adminPanel.stripeProductsAndPricing')}
          </h2>

          {stripeProducts.length === 0 ? (
            <p className='text-gray-500'>{t('adminPanel.loadingProducts')}</p>
          ) : (
            <div className='space-y-6'>
              {stripeProducts.map((product) => (
                <div
                  key={product.id}
                  className='border border-gray-200 rounded-lg p-6'
                >
                  <div className='flex justify-between items-start mb-4'>
                    <div>
                      <h3 className='text-lg font-semibold text-gray-900'>
                        {product.name}
                      </h3>
                      <p className='text-gray-600 text-sm mt-1'>
                        {product.description}
                      </p>
                    </div>
                  </div>

                  <div className='space-y-3'>
                    <div className='text-sm'>
                      <strong className='text-gray-700'>{t('adminPanel.currentPrice')}:</strong>
                      {product.prices.length > 0 ? (
                        <div className='ml-4 mt-2'>
                          <div className='text-sm text-gray-600 bg-gray-50 p-2 rounded'>
                            <span className='font-medium'>
                              ${(product.prices[0].amount / 100).toFixed(2)}{' '}
                              {product.prices[0].currency.toUpperCase()}
                            </span>
                            {product.prices[0].nickname && (
                              <span className='ml-2 text-gray-500'>
                                ({product.prices[0].nickname})
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className='text-gray-500 ml-2'>
                          {t('adminPanel.noActivePrices')}
                        </span>
                      )}
                    </div>

                    <div className='flex items-center space-x-4 pt-3 border-t border-gray-100'>
                      <label className='text-sm font-medium text-gray-700'>
                        {t('adminPanel.updatePrice')}:
                      </label>
                      <input
                        type='number'
                        step='0.01'
                        min='0'
                        value={productPrices[product.id] || ''}
                        onChange={(e) =>
                          setProductPrices({
                            ...productPrices,
                            [product.id]: parseFloat(e.target.value) || 0,
                          })
                        }
                        className='border border-gray-300 rounded px-3 py-2 w-32 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                        placeholder='0.00'
                      />
                      <button
                        onClick={() => handleUpdatePrice(product.id)}
                        disabled={loading}
                        className='px-4 py-2 bg-[#1b3968] text-white rounded text-sm hover:bg-[#1b3968]/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                      >
                        {loading ? t('adminPanel.updating') : t('adminPanel.updatePriceButton')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Goalie Management Section */}
        <div className='bg-white rounded-lg shadow-md p-8 mt-8'>
          <div className='flex items-center mb-6'>
            <Users className='h-6 w-6 text-[#1b3968] mr-3' />
            <h2 className='text-xl font-bold text-gray-900'>
              Goalie Management
            </h2>
          </div>

          {/* Breadcrumb Navigation */}
          <div className='mb-6 bg-gray-50 p-4 rounded-lg'>
            <nav className='flex items-center space-x-2 text-sm'>
              <button
                onClick={navigateToGoalies}
                className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors ${
                  currentStep === 'goalies' 
                    ? 'bg-[#1b3968] text-white' 
                    : 'text-[#1b3968] hover:bg-blue-100'
                }`}
              >
                <Home className='h-4 w-4' />
                Goalies
              </button>
              
              {selectedGoalie && (
                <>
                  <ChevronDown className='h-4 w-4 text-gray-400 rotate-[-90deg]' />
                  <button
                    onClick={navigateToGames}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors ${
                      currentStep === 'games' || currentStep === 'add-game'
                        ? 'bg-[#1b3968] text-white' 
                        : 'text-[#1b3968] hover:bg-blue-100'
                    }`}
                  >
                    <Calendar className='h-4 w-4' />
                    Games ({selectedGoalie.firstName})
                  </button>
                </>
              )}
              
              {selectedGame && (
                <>
                  <ChevronDown className='h-4 w-4 text-gray-400 rotate-[-90deg]' />
                  <button
                    onClick={navigateToGoals}
                    className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors ${
                      currentStep === 'goals' || currentStep === 'add-goal'
                        ? 'bg-[#1b3968] text-white' 
                        : 'text-[#1b3968] hover:bg-blue-100'
                    }`}
                  >
                    <Target className='h-4 w-4' />
                    Goals (vs {selectedGame.opponent})
                  </button>
                </>
              )}
              
              {editingGoal && (
                <>
                  <ChevronDown className='h-4 w-4 text-gray-400 rotate-[-90deg]' />
                  <span className='bg-blue-600 text-white px-3 py-1 rounded-md flex items-center gap-1'>
                    <Edit className='h-4 w-4' />
                    Edit Goal
                  </span>
                </>
              )}
              
              {currentStep === 'add-game' && (
                <>
                  <ChevronDown className='h-4 w-4 text-gray-400 rotate-[-90deg]' />
                  <span className='bg-green-600 text-white px-3 py-1 rounded-md flex items-center gap-1'>
                    <Plus className='h-4 w-4' />
                    Add Game
                  </span>
                </>
              )}
              
              {currentStep === 'add-goal' && (
                <>
                  <ChevronDown className='h-4 w-4 text-gray-400 rotate-[-90deg]' />
                  <span className='bg-green-600 text-white px-3 py-1 rounded-md flex items-center gap-1'>
                    <Plus className='h-4 w-4' />
                    Add Goal
                  </span>
                </>
              )}
            </nav>
          </div>

          {/* Step 1: Goalie Selection */}
          {currentStep === 'goalies' && (
            <>
              {/* Goalie Search */}
              <div className='mb-6'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
                  <input
                    type='text'
                    placeholder='Search goalies by name or team...'
                    value={goalieSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  />
                </div>
              </div>
            </>
          )}

          {/* Selected Goalie */}
          {selectedGoalie && (
            <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
              <div className='flex items-center justify-between'>
                <div>
                  <h3 className='font-semibold text-gray-900'>
                    Selected Goalie: {selectedGoalie.firstName} {selectedGoalie.lastName}
                  </h3>
                  <p className='text-sm text-gray-600'>
                    {selectedGoalie.team} - {selectedGoalie.level}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedGoalie(null);
                    setShowGameForm(false);
                    setShowGoalManagement(false);
                    setShowGameDetails(false);
                    setShowGoalForm(false);
                    setEditingGoal(null);
                  }}
                  className='text-blue-600 hover:text-blue-800 text-sm'
                >
                  Change Selection
                </button>
              </div>
            </div>
          )}

          {/* Continue Step 1: Goalie List and Pagination */}
          {currentStep === 'goalies' && (
            <>
              {/* Pagination Info */}
              {!goaliesLoading && totalGoalies > 0 && (
                <div className='flex justify-between items-center mb-4 text-sm text-gray-600'>
                  <span>
                    Showing {startIndex}-{endIndex} of {totalGoalies} goalies
                  </span>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
              )}

              {/* Goalies List */}
              {goaliesLoading ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#1b3968] mx-auto mb-4'></div>
                  <p className='text-gray-500'>Loading goalies...</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  {goalies.length === 0 ? (
                    <p className='text-gray-500 text-center py-4'>
                      {goalieSearch ? 'No goalies found matching your search.' : 'No goalies registered yet.'}
                    </p>
                  ) : (
                    goalies.map((goalie) => (
                      <div
                        key={goalie.id}
                        className='border rounded-lg p-4 cursor-pointer transition-colors border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        onClick={() => handleGoalieSelect(goalie)}
                      >
                        <div className='flex justify-between items-start'>
                          <div>
                            <h3 className='font-semibold text-gray-900'>
                              {goalie.firstName} {goalie.lastName}
                            </h3>
                            <div className='text-sm text-gray-600 mt-1'>
                              <p>Team: {goalie.team || 'Not specified'}</p>
                              <p>Level: {goalie.level || 'Not specified'}</p>
                              <p>Age: {goalie.age || 'Not specified'}</p>
                            </div>
                          </div>
                          <div className='text-sm text-gray-500'>
                            <p>Games: {goalie.gamesCreated || 0}</p>
                            <p>Analyses: {goalie.analysesCreated || 0}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Pagination Controls */}
              {!goaliesLoading && totalPages > 1 && (
                <div className='mt-6 flex items-center justify-between'>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1b3968] disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <ChevronLeft className='h-4 w-4' />
                    Previous
                  </button>

                  <div className='flex items-center gap-2'>
                    {/* Show page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show current page, first, last, and adjacent pages
                        return page === 1 || 
                               page === totalPages || 
                               Math.abs(page - currentPage) <= 1;
                      })
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {/* Add ellipsis if there's a gap */}
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className='px-2 py-1 text-gray-500'>...</span>
                          )}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968] ${
                              page === currentPage
                                ? 'bg-[#1b3968] text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))
                    }
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#1b3968] disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    Next
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Step 2: Game Management */}
          {currentStep === 'games' && selectedGoalie && (
            <div className='space-y-6'>
              <div className='bg-blue-50 p-4 rounded-lg'>
                <h3 className='font-semibold text-gray-900 mb-2'>
                  Managing Games for: {selectedGoalie.firstName} {selectedGoalie.lastName}
                </h3>
                <p className='text-sm text-gray-600'>
                  {selectedGoalie.team} - {selectedGoalie.level}
                </p>
              </div>

              <div className='flex gap-4 mb-6'>
                <button
                  onClick={handleAddGame}
                  className='flex items-center gap-2 px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90 focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                >
                  <Plus className='h-4 w-4' />
                  Add New Game
                </button>
              </div>

              {/* Games List */}
              {gamesLoading ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#1b3968] mx-auto mb-4'></div>
                  <p className='text-gray-500'>Loading games...</p>
                </div>
              ) : goalieGames.length === 0 ? (
                <div className='text-center py-8 bg-gray-50 rounded-lg'>
                  <Calendar className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                  <p className='text-gray-500'>No games found for this goalie.</p>
                  <p className='text-sm text-gray-400 mt-2'>Click "Add New Game" to create the first game.</p>
                </div>
              ) : (
                <>
                  {/* Games Pagination Info */}
                  {totalGames > 0 && (
                    <div className='flex justify-between items-center mb-4 text-sm text-gray-600'>
                      <span>
                        Showing {gamesStartIndex}-{gamesEndIndex} of {totalGames} games
                      </span>
                      <span>
                        Page {gamesCurrentPage} of {gamesTotalPages}
                      </span>
                    </div>
                  )}

                  <div className='space-y-3'>
                    {goalieGames.map((game) => (
                      <div
                        key={game.id}
                        className='border rounded-lg p-4 cursor-pointer transition-colors border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        onClick={() => handleGameSelect(game)}
                      >
                        <div className='flex justify-between items-start'>
                          <div>
                            <h4 className='font-semibold text-gray-900'>
                              vs {game.opponent}
                            </h4>
                            <div className='text-sm text-gray-600 mt-1'>
                              <p>Date: {new Date(game.date).toLocaleDateString()}</p>
                              <p>Type: {game.competitionType.replace('-', ' ')}</p>
                              {game.shotsFaced && <p>Shots Faced: {game.shotsFaced}</p>}
                            </div>
                          </div>
                          <div className='text-right flex flex-col items-end gap-2'>
                            {game.outcome && (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                game.outcome.includes('win') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {game.outcome.toUpperCase().replace('-', ' ')}
                              </span>
                            )}
                            <button
                              onClick={(e) => handleDeleteGameClick(game, e)}
                              className='text-gray-400 hover:text-red-600 transition-colors p-1 rounded-md hover:bg-red-50'
                              title='Delete game'
                            >
                              <Trash2 className='h-4 w-4' />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Games Pagination Controls */}
                  {gamesTotalPages > 1 && (
                    <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-200'>
                      <div className='text-sm text-gray-500'>
                        Showing {gamesStartIndex} to {gamesEndIndex} of {totalGames} games
                      </div>
                      <div className='flex items-center space-x-2'>
                        <button
                          onClick={() => {
                            const newPage = Math.max(gamesCurrentPage - 1, 1);
                            setGamesCurrentPage(newPage);
                            if (selectedGoalie) fetchGoalieGames(selectedGoalie.id, newPage);
                          }}
                          disabled={gamesCurrentPage === 1}
                          className='p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50'
                        >
                          <ChevronLeft className='h-4 w-4' />
                        </button>
                        <span className='text-sm font-medium'>
                          Page {gamesCurrentPage} of {gamesTotalPages}
                        </span>
                        <button
                          onClick={() => {
                            const newPage = Math.min(gamesCurrentPage + 1, gamesTotalPages);
                            setGamesCurrentPage(newPage);
                            if (selectedGoalie) fetchGoalieGames(selectedGoalie.id, newPage);
                          }}
                          disabled={gamesCurrentPage === gamesTotalPages}
                          className='p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50'
                        >
                          <ChevronRight className='h-4 w-4' />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 3: Add Game Form */}
          {currentStep === 'add-game' && selectedGoalie && (
            <div className='mt-6 p-6 bg-gray-50 border border-gray-200 rounded-lg'>
              <div className='flex items-center justify-between mb-4'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Create Game for {selectedGoalie.firstName} {selectedGoalie.lastName}
                </h3>
                <button
                  onClick={() => {
                    setCurrentStep('games');
                    setShowGameForm(false);
                  }}
                  className='text-gray-500 hover:text-gray-700'
                >
                  Cancel
                </button>
              </div>

              <form onSubmit={handleCreateGame} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='gameDate' className='block text-sm font-medium text-gray-700 mb-2'>
                      Game Date
                    </label>
                    <input
                      type='date'
                      id='gameDate'
                      value={gameDate}
                      onChange={(e) => setGameDate(e.target.value)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor='opponent' className='block text-sm font-medium text-gray-700 mb-2'>
                      Opponent
                    </label>
                    <input
                      type='text'
                      id='opponent'
                      value={opponent}
                      onChange={(e) => setOpponent(e.target.value)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                      placeholder='Team name'
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor='competitionType' className='block text-sm font-medium text-gray-700 mb-2'>
                    Competition Type
                  </label>
                  <select
                    id='competitionType'
                    value={competitionType}
                    onChange={(e) => setCompetitionType(e.target.value as typeof competitionType)}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                  >
                    <option value='regular-season'>Regular Season</option>
                    <option value='playoff'>Playoff</option>
                    <option value='tournament'>Tournament</option>
                    <option value='exhibition'>Exhibition</option>
                    <option value='practice'>Practice</option>
                  </select>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='shotsFaced' className='block text-sm font-medium text-gray-700 mb-2'>
                      Shots Faced (Optional)
                    </label>
                    <input
                      type='number'
                      id='shotsFaced'
                      value={shotsFaced}
                      onChange={(e) => setShotsFaced(e.target.value)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                      placeholder='0'
                      min='0'
                    />
                  </div>

                  <div>
                    <label htmlFor='saves' className='block text-sm font-medium text-gray-700 mb-2'>
                      Saves (Optional)
                    </label>
                    <input
                      type='number'
                      id='saves'
                      value={saves}
                      onChange={(e) => setSaves(e.target.value)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1b3968]'
                      placeholder='0'
                      min='0'
                    />
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Game Outcome (Optional)
                  </label>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
                    {['win', 'loss', 'win-ot', 'loss-ot', 'win-so', 'loss-so'].map((outcomeOption) => (
                      <button
                        key={outcomeOption}
                        type='button'
                        onClick={() => setOutcome(outcomeOption as typeof outcome)}
                        className={`p-2 rounded-md border text-sm font-medium transition-colors ${
                          outcome === outcomeOption
                            ? 'border-[#1b3968] bg-blue-50 text-[#1b3968]'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {outcomeOption.replace('-', ' ').toUpperCase()}
                      </button>
                    ))}
                  </div>
                  <button
                    type='button'
                    onClick={() => setOutcome(null)}
                    className='mt-2 text-sm text-gray-500 hover:text-gray-700'
                  >
                    Clear Selection
                  </button>
                </div>

                <div className='flex gap-4 pt-4 border-t'>
                  <button
                    type='submit'
                    disabled={loading}
                    className='flex items-center gap-2 px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90 focus:outline-none focus:ring-2 focus:ring-[#1b3968] disabled:opacity-50'
                  >
                    {loading ? (
                      <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <Calendar className='h-4 w-4' />
                    )}
                    {loading ? 'Creating...' : 'Create Game'}
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setCurrentStep('games');
                      setShowGameForm(false);
                    }}
                    className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 4: Goals List */}
          {currentStep === 'goals' && selectedGame && (
            <div className='space-y-6'>
              <div className='bg-green-50 p-4 rounded-lg'>
                <h3 className='font-semibold text-gray-900 mb-2'>
                  Managing Goals for Game: vs {selectedGame.opponent}
                </h3>
                <p className='text-sm text-gray-600'>
                  Date: {new Date(selectedGame.date).toLocaleDateString()} - {selectedGame.competitionType.replace('-', ' ')}
                </p>
              </div>

              <div className='flex gap-4 mb-6'>
                <button
                  onClick={handleAddGoal}
                  className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600'
                >
                  <Plus className='h-4 w-4' />
                  Add New Goal
                </button>
              </div>

              {/* Goals List */}
              {goalsLoading ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4'></div>
                  <p className='text-gray-500'>Loading goals...</p>
                </div>
              ) : gameGoals.length === 0 ? (
                <div className='text-center py-8 bg-gray-50 rounded-lg'>
                  <Target className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                  <p className='text-gray-500'>No goals found for this game.</p>
                  <p className='text-sm text-gray-400 mt-2'>Click "Add New Goal" to create the first goal.</p>
                </div>
              ) : (
                <>
                  <div className='space-y-3'>
                    {paginatedGoals.map((goal) => (
                      <div
                        key={goal.id}
                        className='border rounded-lg p-4 cursor-pointer transition-colors border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        onClick={() => handleGoalEdit(goal)}
                      >
                        <div className='flex justify-between items-start'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2'>
                              <h4 className='font-semibold text-gray-900'>{goal.title}</h4>
                              <Edit className='h-4 w-4 text-gray-400' />
                            </div>
                            {goal.situationDescription && (
                              <p className='text-sm text-gray-600 mt-1'>{goal.situationDescription}</p>
                            )}
                            {goal.shotType && (
                              <p className='text-xs text-gray-500 mt-1'>{goal.shotType}</p>
                            )}
                          </div>
                          <div className='flex flex-col items-end gap-2'>
                            <span className={`text-sm font-medium ${
                              goal.result === 'goal' ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {goal.result === 'goal' ? 'Goal' : 'Save'}
                            </span>
                            <button
                              onClick={(e) => handleDeleteGoalClick(goal, e)}
                              className='text-gray-400 hover:text-red-600 transition-colors p-1 rounded-md hover:bg-red-50'
                              title='Delete goal'
                            >
                              <Trash2 className='h-4 w-4' />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Goals Pagination */}
                  {goalsTotalPages > 1 && (
                    <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-200'>
                      <div className='text-sm text-gray-500'>
                        Showing {goalsStartIndex + 1} to {goalsEndIndex} of {totalGoals} goals
                      </div>
                      <div className='flex items-center space-x-2'>
                        <button
                          onClick={() => setCurrentGoalsPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentGoalsPage === 1}
                          className='p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50'
                        >
                          <ChevronLeft className='h-4 w-4' />
                        </button>
                        <span className='text-sm font-medium'>
                          Page {currentGoalsPage} of {goalsTotalPages}
                        </span>
                        <button
                          onClick={() => setCurrentGoalsPage(prev => Math.min(prev + 1, goalsTotalPages))}
                          disabled={currentGoalsPage === goalsTotalPages}
                          className='p-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50'
                        >
                          <ChevronRight className='h-4 w-4' />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 5: Edit Goal Form */}
          {currentStep === 'edit-goal' && editingGoal && (
            <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
              <div className='flex items-center justify-between mb-4'>
                <h6 className='font-semibold text-gray-900'>
                  Edit Goal: {editingGoal.title}
                </h6>
                <button
                  onClick={() => {
                    setCurrentStep('goals');
                    setEditingGoal(null);
                  }}
                  className='text-gray-500 hover:text-gray-700'
                >
                  <X className='h-5 w-5' />
                </button>
              </div>
              
              <form onSubmit={handleUpdateGoal} className='space-y-4'>
                <div>
                  <label htmlFor='editGoalTitle' className='block text-sm font-medium text-gray-700 mb-2'>
                    Goal Title
                  </label>
                  <input
                    type='text'
                    id='editGoalTitle'
                    value={editGoalTitle}
                    onChange={(e) => setEditGoalTitle(e.target.value)}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                    placeholder='e.g., Power play goal, Breakaway'
                    required
                  />
                </div>

                <div>
                  <label htmlFor='editGoalDescription' className='block text-sm font-medium text-gray-700 mb-2'>
                    Situation Description
                  </label>
                  <textarea
                    id='editGoalDescription'
                    value={editGoalDescription}
                    onChange={(e) => setEditGoalDescription(e.target.value)}
                    rows={3}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                    placeholder='Describe what happened during this goal...'
                    required
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='editShotType' className='block text-sm font-medium text-gray-700 mb-2'>
                      Shot Type
                    </label>
                    <select
                      id='editShotType'
                      value={editShotType || ''}
                      onChange={(e) => setEditShotType(e.target.value as ShotType || null)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                    >
                      <option value=''>Select shot type...</option>
                      {shotTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor='editGameSituation' className='block text-sm font-medium text-gray-700 mb-2'>
                      Game Situation
                    </label>
                    <select
                      id='editGameSituation'
                      value={editGameSituation || ''}
                      onChange={(e) => setEditGameSituation(e.target.value as GameSituation || null)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                    >
                      <option value=''>Select situation...</option>
                      {gameSituationOptions.map((situation) => (
                        <option key={situation} value={situation}>
                          {situation}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Goal Position Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Goal Position
                  </label>
                  <p className='text-xs text-gray-600 mb-3'>
                    Click on the goal diagram to set the goal position
                  </p>
                  <GoalDiagram
                    onPositionSelect={(x, y) => {
                      setEditXPosition(x);
                      setEditYPosition(y);
                    }}
                    provisionalPosition={{ x: editXPosition, y: editYPosition }}
                    width={300}
                    height={200}
                    hideNumbers={true}
                  />
                  <div className='text-xs text-gray-500 mt-2 text-center'>
                    Selected position: ({editXPosition.toFixed(1)}%, {editYPosition.toFixed(1)}%)
                  </div>
                </div>

                {/* Shot Position Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Shot Position (Optional)
                  </label>
                  <p className='text-xs text-gray-600 mb-3'>
                    Click on the rink diagram to set where the shot was taken from
                  </p>
                  <ShotPositionDiagram
                    onPositionSelect={(x, y) => {
                      setEditShotXPosition(x);
                      setEditShotYPosition(y);
                    }}
                    provisionalPosition={
                      editShotXPosition && editShotYPosition 
                        ? { x: editShotXPosition, y: editShotYPosition } 
                        : null
                    }
                    width={300}
                    height={200}
                    hideNumbers={true}
                  />
                  <div className='flex items-center justify-center gap-4 mt-2'>
                    <div className='text-xs text-gray-500'>
                      {editShotXPosition && editShotYPosition 
                        ? `Selected position: (${editShotXPosition.toFixed(1)}%, ${editShotYPosition.toFixed(1)}%)` 
                        : 'No shot position selected'
                      }
                    </div>
                    {editShotXPosition && editShotYPosition && (
                      <button
                        type='button'
                        onClick={() => {
                          setEditShotXPosition(null);
                          setEditShotYPosition(null);
                        }}
                        className='text-xs text-red-600 hover:text-red-800 underline'
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Result
                  </label>
                  <div className='flex gap-4'>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        value='goal'
                        checked={editGoalResult === 'goal'}
                        onChange={(e) => setEditGoalResult(e.target.value as 'save' | 'goal')}
                        className='mr-2'
                      />
                      <span className='text-sm text-red-600 font-medium'>Goal</span>
                    </label>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        value='save'
                        checked={editGoalResult === 'save'}
                        onChange={(e) => setEditGoalResult(e.target.value as 'save' | 'goal')}
                        className='mr-2'
                      />
                      <span className='text-sm text-green-600 font-medium'>Save</span>
                    </label>
                  </div>
                </div>

                <div className='flex gap-3 pt-4 border-t'>
                  <button
                    type='submit'
                    disabled={loading}
                    className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50'
                  >
                    {loading ? (
                      <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <Check className='h-4 w-4' />
                    )}
                    {loading ? 'Updating...' : 'Update Goal'}
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setCurrentStep('goals');
                      setEditingGoal(null);
                    }}
                    className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 6: Add Goal Form */}
          {currentStep === 'add-goal' && selectedGame && (
            <div className='p-4 bg-green-50 border border-green-200 rounded-lg'>
              <div className='flex items-center justify-between mb-4'>
                <h6 className='font-semibold text-gray-900'>
                  Add Goal: vs {selectedGame.opponent}
                </h6>
                <button
                  onClick={() => {
                    setCurrentStep('goals');
                    setShowGoalForm(false);
                  }}
                  className='text-gray-500 hover:text-gray-700'
                >
                  <X className='h-5 w-5' />
                </button>
              </div>
              
              <form onSubmit={handleCreateGoal} className='space-y-4'>
                <div>
                  <label htmlFor='goalTitle' className='block text-sm font-medium text-gray-700 mb-2'>
                    Goal Title
                  </label>
                  <input
                    type='text'
                    id='goalTitle'
                    value={goalTitle}
                    onChange={(e) => setGoalTitle(e.target.value)}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                    placeholder='e.g., Power play goal, Breakaway'
                    required
                  />
                </div>

                <div>
                  <label htmlFor='goalDescription' className='block text-sm font-medium text-gray-700 mb-2'>
                    Situation Description
                  </label>
                  <textarea
                    id='goalDescription'
                    value={goalDescription}
                    onChange={(e) => setGoalDescription(e.target.value)}
                    rows={3}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                    placeholder='Describe what happened during this goal...'
                    required
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <label htmlFor='shotType' className='block text-sm font-medium text-gray-700 mb-2'>
                      Shot Type
                    </label>
                    <select
                      id='shotType'
                      value={shotType || ''}
                      onChange={(e) => setShotType(e.target.value as ShotType || null)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                    >
                      <option value=''>Select shot type...</option>
                      {shotTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor='gameSituation' className='block text-sm font-medium text-gray-700 mb-2'>
                      Game Situation
                    </label>
                    <select
                      id='gameSituation'
                      value={gameSituation || ''}
                      onChange={(e) => setGameSituation(e.target.value as GameSituation || null)}
                      className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                    >
                      <option value=''>Select situation...</option>
                      {gameSituationOptions.map((situation) => (
                        <option key={situation} value={situation}>
                          {situation}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Goal Position Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Goal Position
                  </label>
                  <p className='text-xs text-gray-600 mb-3'>
                    Click on the goal diagram to set the goal position
                  </p>
                  <GoalDiagram
                    onPositionSelect={(x, y) => {
                      setXPosition(x);
                      setYPosition(y);
                    }}
                    provisionalPosition={{ x: xPosition, y: yPosition }}
                    width={350}
                    height={250}
                    hideNumbers={true}
                  />
                  <div className='text-xs text-gray-500 mt-2 text-center'>
                    Selected position: ({xPosition.toFixed(1)}%, {yPosition.toFixed(1)}%)
                  </div>
                </div>

                {/* Shot Position Selection */}
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Shot Position (Optional)
                  </label>
                  <p className='text-xs text-gray-600 mb-3'>
                    Click on the rink diagram to set where the shot was taken from
                  </p>
                  <ShotPositionDiagram
                    onPositionSelect={(x, y) => {
                      setShotXPosition(x);
                      setShotYPosition(y);
                    }}
                    provisionalPosition={
                      shotXPosition && shotYPosition 
                        ? { x: shotXPosition, y: shotYPosition } 
                        : null
                    }
                    width={350}
                    height={250}
                    hideNumbers={true}
                  />
                  <div className='flex items-center justify-center gap-4 mt-2'>
                    <div className='text-xs text-gray-500'>
                      {shotXPosition && shotYPosition 
                        ? `Selected position: (${shotXPosition.toFixed(1)}%, ${shotYPosition.toFixed(1)}%)` 
                        : 'No shot position selected'
                      }
                    </div>
                    {shotXPosition && shotYPosition && (
                      <button
                        type='button'
                        onClick={() => {
                          setShotXPosition(null);
                          setShotYPosition(null);
                        }}
                        className='text-xs text-red-600 hover:text-red-800 underline'
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2'>
                    Result
                  </label>
                  <div className='flex gap-4'>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        value='goal'
                        checked={goalResult === 'goal'}
                        onChange={(e) => setGoalResult(e.target.value as 'save' | 'goal')}
                        className='mr-2'
                      />
                      <span className='text-sm text-red-600 font-medium'>Goal</span>
                    </label>
                    <label className='flex items-center'>
                      <input
                        type='radio'
                        value='save'
                        checked={goalResult === 'save'}
                        onChange={(e) => setGoalResult(e.target.value as 'save' | 'goal')}
                        className='mr-2'
                      />
                      <span className='text-sm text-green-600 font-medium'>Save</span>
                    </label>
                  </div>
                </div>

                <div className='flex gap-3 pt-4 border-t'>
                  <button
                    type='submit'
                    disabled={loading}
                    className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-50'
                  >
                    {loading ? (
                      <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                    ) : (
                      <Plus className='h-4 w-4' />
                    )}
                    {loading ? 'Creating...' : 'Create Goal'}
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setCurrentStep('goals');
                      setShowGoalForm(false);
                    }}
                    className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Old Goal Management Section - Will be phased out */}
          {showGoalManagement && selectedGoalie && (
            <div className='mt-6 p-6 bg-gray-50 border border-gray-200 rounded-lg'>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-3'>
                  <Trophy className='h-5 w-5 text-green-600' />
                  <h3 className='text-lg font-semibold text-gray-900'>
                    Manage Goals for {selectedGoalie.firstName} {selectedGoalie.lastName}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowGoalManagement(false);
                    setSelectedGame(null);
                    setGoalieGames([]);
                    setGamesCurrentPage(1);
                    setTotalGames(0);
                  }}
                  className='flex items-center gap-2 text-gray-500 hover:text-gray-700'
                >
                  <ArrowLeft className='h-4 w-4' />
                  Back
                </button>
              </div>

              {/* Games Pagination Info */}
              {!gamesLoading && totalGames > 0 && (
                <div className='flex justify-between items-center mb-4 text-sm text-gray-600'>
                  <span>
                    Showing {gamesStartIndex}-{gamesEndIndex} of {totalGames} games
                  </span>
                  <span>
                    Page {gamesCurrentPage} of {gamesTotalPages}
                  </span>
                </div>
              )}

              {/* Games List */}
              {gamesLoading ? (
                <div className='text-center py-8'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4'></div>
                  <p className='text-gray-500'>Loading games...</p>
                </div>
              ) : totalGames === 0 ? (
                <div className='text-center py-8 bg-white rounded-lg border border-gray-200'>
                  <Trophy className='h-12 w-12 text-gray-400 mx-auto mb-4' />
                  <p className='text-gray-500'>No games found for this goalie.</p>
                  <p className='text-sm text-gray-400 mt-1'>Create a game first to add goals.</p>
                </div>
              ) : (
                <div className='space-y-3'>
                  <h4 className='font-medium text-gray-700 mb-3'>
                    Select a game to manage goals:
                  </h4>
                  {goalieGames.map((game) => (
                    <div
                      key={game.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedGame?.id === game.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                      }`}
                      onClick={() => setSelectedGame(game)}
                    >
                      <div className='flex justify-between items-start'>
                        <div>
                          <h5 className='font-semibold text-gray-900'>
                            vs {game.opponent}
                          </h5>
                          <div className='text-sm text-gray-600 mt-1'>
                            <p>Date: {game.date.toLocaleDateString()}</p>
                            <p>Competition: {game.competitionType.replace('-', ' ')}</p>
                            {game.outcome && (
                              <p>Outcome: <span className='capitalize'>{game.outcome.replace('-', ' ')}</span></p>
                            )}
                          </div>
                        </div>
                        <div className='text-sm text-gray-500 text-right flex flex-col items-end gap-2'>
                          <div>
                            {game.shotsFaced !== null && game.saves !== null && (
                              <p>{game.saves}/{game.shotsFaced} saves</p>
                            )}
                            {game.shotsFaced !== null && game.saves !== null && (
                              <p>Save %: {((game.saves / game.shotsFaced) * 100).toFixed(1)}%</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => handleDeleteGameClick(game, e)}
                            className='text-gray-400 hover:text-red-600 transition-colors p-1 rounded-md hover:bg-red-50'
                            title='Delete game'
                          >
                            <Trash2 className='h-4 w-4' />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Games Pagination Controls */}
                  {gamesTotalPages > 1 && (
                    <div className='mt-6 flex items-center justify-between'>
                      <button
                        onClick={() => handleGamesPageChange(gamesCurrentPage - 1)}
                        disabled={gamesCurrentPage === 1}
                        className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        <ChevronLeft className='h-4 w-4' />
                        Previous
                      </button>

                      <div className='flex items-center gap-2'>
                        {Array.from({ length: gamesTotalPages }, (_, i) => i + 1)
                          .filter(page => {
                            return page === 1 || 
                                   page === gamesTotalPages || 
                                   Math.abs(page - gamesCurrentPage) <= 1;
                          })
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && (
                                <span className='px-2 py-1 text-gray-500'>...</span>
                              )}
                              <button
                                onClick={() => handleGamesPageChange(page)}
                                className={`px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-green-600 ${
                                  page === gamesCurrentPage
                                    ? 'bg-green-600 text-white'
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))
                        }
                      </div>

                      <button
                        onClick={() => handleGamesPageChange(gamesCurrentPage + 1)}
                        disabled={gamesCurrentPage === gamesTotalPages}
                        className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed'
                      >
                        Next
                        <ChevronRight className='h-4 w-4' />
                      </button>
                    </div>
                  )}

                  {/* Goal Management for Selected Game */}
                  {selectedGame && !showGoalForm && !showGameDetails && (
                    <div className='mt-6 p-4 bg-white border border-green-200 rounded-lg'>
                      <h5 className='font-semibold text-gray-900 mb-3'>
                        Goal Management for: vs {selectedGame.opponent}
                      </h5>
                      <div className='flex gap-3'>
                        <button
                          onClick={() => {
                            setShowGameDetails(true);
                            fetchGameGoals(selectedGame.id);
                          }}
                          className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600'
                        >
                          <Target className='h-4 w-4' />
                          View Game Details
                        </button>
                        <button
                          onClick={() => setShowGoalForm(true)}
                          className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600'
                        >
                          <Plus className='h-4 w-4' />
                          Add Goal
                        </button>
                      </div>
                      <p className='text-sm text-gray-500 mt-2'>
                        Click "View Game Details" to see existing goals or "Add Goal" to create a new goal for this game.
                      </p>
                    </div>
                  )}

                  {/* Admin Goal Creation Form */}
                  {selectedGame && showGoalForm && (
                    <div className='mt-6 p-4 bg-white border border-green-200 rounded-lg'>
                      <div className='flex items-center justify-between mb-4'>
                        <h5 className='font-semibold text-gray-900'>
                          Add Goal: vs {selectedGame.opponent}
                        </h5>
                        <button
                          onClick={() => {
                            setShowGoalForm(false);
                            setGoalTitle('');
                            setGoalDescription('');
                            setGoalResult('goal');
                            setShotType(null);
                            setGameSituation(null);
                            setXPosition(50);
                            setYPosition(50);
                            setShotXPosition(null);
                            setShotYPosition(null);
                          }}
                          className='text-gray-500 hover:text-gray-700'
                        >
                          <X className='h-5 w-5' />
                        </button>
                      </div>

                      <form onSubmit={handleCreateGoal} className='space-y-4'>
                        <div>
                          <label htmlFor='goalTitle' className='block text-sm font-medium text-gray-700 mb-2'>
                            Goal Title
                          </label>
                          <input
                            type='text'
                            id='goalTitle'
                            value={goalTitle}
                            onChange={(e) => setGoalTitle(e.target.value)}
                            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                            placeholder='e.g., Power play goal, Breakaway'
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor='goalDescription' className='block text-sm font-medium text-gray-700 mb-2'>
                            Situation Description
                          </label>
                          <textarea
                            id='goalDescription'
                            value={goalDescription}
                            onChange={(e) => setGoalDescription(e.target.value)}
                            rows={3}
                            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                            placeholder='Describe what happened during this goal...'
                            required
                          />
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                          <div>
                            <label htmlFor='shotType' className='block text-sm font-medium text-gray-700 mb-2'>
                              Shot Type
                            </label>
                            <select
                              id='shotType'
                              value={shotType || ''}
                              onChange={(e) => setShotType(e.target.value as ShotType || null)}
                              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                            >
                              <option value=''>Select shot type...</option>
                              {shotTypeOptions.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label htmlFor='gameSituation' className='block text-sm font-medium text-gray-700 mb-2'>
                              Game Situation
                            </label>
                            <select
                              id='gameSituation'
                              value={gameSituation || ''}
                              onChange={(e) => setGameSituation(e.target.value as GameSituation || null)}
                              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600'
                            >
                              <option value=''>Select situation...</option>
                              {gameSituationOptions.map((situation) => (
                                <option key={situation} value={situation}>
                                  {situation}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Goal Position Selection */}
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Goal Position
                          </label>
                          <p className='text-xs text-gray-600 mb-3'>
                            Click on the goal diagram to set the goal position
                          </p>
                          <GoalDiagram
                            onPositionSelect={(x, y) => {
                              setXPosition(x);
                              setYPosition(y);
                            }}
                            provisionalPosition={{ x: xPosition, y: yPosition }}
                            width={350}
                            height={250}
                            hideNumbers={true}
                          />
                          <div className='text-xs text-gray-500 mt-2 text-center'>
                            Selected position: ({xPosition.toFixed(1)}%, {yPosition.toFixed(1)}%)
                          </div>
                        </div>

                        {/* Shot Position Selection */}
                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Shot Position (Optional)
                          </label>
                          <p className='text-xs text-gray-600 mb-3'>
                            Click on the rink diagram to set where the shot was taken from
                          </p>
                          <ShotPositionDiagram
                            onPositionSelect={(x, y) => {
                              setShotXPosition(x);
                              setShotYPosition(y);
                            }}
                            provisionalPosition={
                              shotXPosition && shotYPosition 
                                ? { x: shotXPosition, y: shotYPosition } 
                                : null
                            }
                            width={350}
                            height={250}
                            hideNumbers={true}
                          />
                          <div className='flex items-center justify-center gap-4 mt-2'>
                            <div className='text-xs text-gray-500'>
                              {shotXPosition && shotYPosition 
                                ? `Selected position: (${shotXPosition.toFixed(1)}%, ${shotYPosition.toFixed(1)}%)` 
                                : 'No shot position selected'
                              }
                            </div>
                            {shotXPosition && shotYPosition && (
                              <button
                                type='button'
                                onClick={() => {
                                  setShotXPosition(null);
                                  setShotYPosition(null);
                                }}
                                className='text-xs text-red-600 hover:text-red-800 underline'
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Result
                          </label>
                          <div className='flex gap-4'>
                            <label className='flex items-center'>
                              <input
                                type='radio'
                                value='goal'
                                checked={goalResult === 'goal'}
                                onChange={(e) => setGoalResult(e.target.value as 'save' | 'goal')}
                                className='mr-2'
                              />
                              <span className='text-sm text-red-600 font-medium'>Goal</span>
                            </label>
                            <label className='flex items-center'>
                              <input
                                type='radio'
                                value='save'
                                checked={goalResult === 'save'}
                                onChange={(e) => setGoalResult(e.target.value as 'save' | 'goal')}
                                className='mr-2'
                              />
                              <span className='text-sm text-green-600 font-medium'>Save</span>
                            </label>
                          </div>
                        </div>

                        <div className='flex gap-3 pt-4 border-t'>
                          <button
                            type='submit'
                            disabled={loading}
                            className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600 disabled:opacity-50'
                          >
                            {loading ? (
                              <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                            ) : (
                              <Plus className='h-4 w-4' />
                            )}
                            {loading ? 'Creating...' : 'Create Goal'}
                          </button>
                          <button
                            type='button'
                            onClick={() => {
                              setShowGoalForm(false);
                              setGoalTitle('');
                              setGoalDescription('');
                              setGoalResult('goal');
                              setShotType(null);
                              setGameSituation(null);
                              setXPosition(50);
                              setYPosition(50);
                              setShotXPosition('');
                              setShotYPosition('');
                            }}
                            className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Game Details View */}
                  {selectedGame && showGameDetails && (
                    <div className='mt-6 p-4 bg-white border border-blue-200 rounded-lg'>
                      <div className='flex items-center justify-between mb-4'>
                        <h5 className='font-semibold text-gray-900'>
                          Game Details: vs {selectedGame.opponent}
                        </h5>
                        <button
                          onClick={() => {
                            setShowGameDetails(false);
                            setGameGoals([]);
                          }}
                          className='text-gray-500 hover:text-gray-700'
                        >
                          <X className='h-5 w-5' />
                        </button>
                      </div>

                      {/* Game Info */}
                      <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
                        <div className='grid grid-cols-2 gap-4 text-sm'>
                          <div>
                            <span className='font-medium text-gray-700'>Date:</span>{' '}
                            <span className='text-gray-900'>{selectedGame.date.toLocaleDateString()}</span>
                          </div>
                          <div>
                            <span className='font-medium text-gray-700'>Competition:</span>{' '}
                            <span className='text-gray-900 capitalize'>{selectedGame.competitionType.replace('-', ' ')}</span>
                          </div>
                          {selectedGame.outcome && (
                            <div>
                              <span className='font-medium text-gray-700'>Outcome:</span>{' '}
                              <span className='text-gray-900 capitalize'>{selectedGame.outcome.replace('-', ' ')}</span>
                            </div>
                          )}
                          {selectedGame.shotsFaced !== null && selectedGame.saves !== null && (
                            <>
                              <div>
                                <span className='font-medium text-gray-700'>Shots/Saves:</span>{' '}
                                <span className='text-gray-900'>{selectedGame.saves}/{selectedGame.shotsFaced}</span>
                              </div>
                              <div>
                                <span className='font-medium text-gray-700'>Save %:</span>{' '}
                                <span className='text-gray-900'>
                                  {((selectedGame.saves / selectedGame.shotsFaced) * 100).toFixed(1)}%
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Goals List */}
                      <div>
                        <h6 className='font-medium text-gray-700 mb-3'>Goals in this game:</h6>
                        {goalsLoading ? (
                          <div className='text-center py-4'>
                            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto'></div>
                          </div>
                        ) : gameGoals.length === 0 ? (
                          <p className='text-gray-500 text-center py-4'>No goals recorded for this game yet.</p>
                        ) : (
                          <div className='space-y-2'>
                            {gameGoals.map((goal) => (
                              <div key={goal.id}>
                                {editingGoal?.id === goal.id ? (
                                  /* Edit Goal Form */
                                  <div className='p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                                    <div className='flex items-center justify-between mb-4'>
                                      <h6 className='font-semibold text-gray-900'>
                                        Edit Goal
                                      </h6>
                                      <button
                                        onClick={handleCancelEdit}
                                        className='text-gray-500 hover:text-gray-700'
                                      >
                                        <X className='h-5 w-5' />
                                      </button>
                                    </div>
                                    
                                    <form onSubmit={handleUpdateGoal} className='space-y-4'>
                                      <div>
                                        <label htmlFor='editGoalTitle' className='block text-sm font-medium text-gray-700 mb-2'>
                                          Goal Title
                                        </label>
                                        <input
                                          type='text'
                                          id='editGoalTitle'
                                          value={editGoalTitle}
                                          onChange={(e) => setEditGoalTitle(e.target.value)}
                                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                                          placeholder='e.g., Power play goal, Breakaway'
                                          required
                                        />
                                      </div>

                                      <div>
                                        <label htmlFor='editGoalDescription' className='block text-sm font-medium text-gray-700 mb-2'>
                                          Situation Description
                                        </label>
                                        <textarea
                                          id='editGoalDescription'
                                          value={editGoalDescription}
                                          onChange={(e) => setEditGoalDescription(e.target.value)}
                                          rows={3}
                                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                                          placeholder='Describe what happened during this goal...'
                                          required
                                        />
                                      </div>

                                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                        <div>
                                          <label htmlFor='editShotType' className='block text-sm font-medium text-gray-700 mb-2'>
                                            Shot Type
                                          </label>
                                          <select
                                            id='editShotType'
                                            value={editShotType || ''}
                                            onChange={(e) => setEditShotType(e.target.value as ShotType || null)}
                                            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                                          >
                                            <option value=''>Select shot type...</option>
                                            {shotTypeOptions.map((type) => (
                                              <option key={type} value={type}>
                                                {type}
                                              </option>
                                            ))}
                                          </select>
                                        </div>

                                        <div>
                                          <label htmlFor='editGameSituation' className='block text-sm font-medium text-gray-700 mb-2'>
                                            Game Situation
                                          </label>
                                          <select
                                            id='editGameSituation'
                                            value={editGameSituation || ''}
                                            onChange={(e) => setEditGameSituation(e.target.value as GameSituation || null)}
                                            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600'
                                          >
                                            <option value=''>Select situation...</option>
                                            {gameSituationOptions.map((situation) => (
                                              <option key={situation} value={situation}>
                                                {situation}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>

                                      {/* Goal Position Selection */}
                                      <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                                          Goal Position
                                        </label>
                                        <p className='text-xs text-gray-600 mb-3'>
                                          Click on the goal diagram to set the goal position
                                        </p>
                                        <GoalDiagram
                                          onPositionSelect={(x, y) => {
                                            setEditXPosition(x);
                                            setEditYPosition(y);
                                          }}
                                          provisionalPosition={{ x: editXPosition, y: editYPosition }}
                                          width={300}
                                          height={200}
                                          hideNumbers={true}
                                        />
                                        <div className='text-xs text-gray-500 mt-2 text-center'>
                                          Selected position: ({editXPosition.toFixed(1)}%, {editYPosition.toFixed(1)}%)
                                        </div>
                                      </div>

                                      {/* Shot Position Selection */}
                                      <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                                          Shot Position (Optional)
                                        </label>
                                        <p className='text-xs text-gray-600 mb-3'>
                                          Click on the rink diagram to set where the shot was taken from
                                        </p>
                                        <ShotPositionDiagram
                                          onPositionSelect={(x, y) => {
                                            setEditShotXPosition(x);
                                            setEditShotYPosition(y);
                                          }}
                                          provisionalPosition={
                                            editShotXPosition && editShotYPosition 
                                              ? { x: editShotXPosition, y: editShotYPosition } 
                                              : null
                                          }
                                          width={300}
                                          height={200}
                                          hideNumbers={true}
                                        />
                                        <div className='flex items-center justify-center gap-4 mt-2'>
                                          <div className='text-xs text-gray-500'>
                                            {editShotXPosition && editShotYPosition 
                                              ? `Selected position: (${editShotXPosition.toFixed(1)}%, ${editShotYPosition.toFixed(1)}%)` 
                                              : 'No shot position selected'
                                            }
                                          </div>
                                          {editShotXPosition && editShotYPosition && (
                                            <button
                                              type='button'
                                              onClick={() => {
                                                setEditShotXPosition(null);
                                                setEditShotYPosition(null);
                                              }}
                                              className='text-xs text-red-600 hover:text-red-800 underline'
                                            >
                                              Clear
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                                          Result
                                        </label>
                                        <div className='flex gap-4'>
                                          <label className='flex items-center'>
                                            <input
                                              type='radio'
                                              value='goal'
                                              checked={editGoalResult === 'goal'}
                                              onChange={(e) => setEditGoalResult(e.target.value as 'save' | 'goal')}
                                              className='mr-2'
                                            />
                                            <span className='text-sm text-red-600 font-medium'>Goal</span>
                                          </label>
                                          <label className='flex items-center'>
                                            <input
                                              type='radio'
                                              value='save'
                                              checked={editGoalResult === 'save'}
                                              onChange={(e) => setEditGoalResult(e.target.value as 'save' | 'goal')}
                                              className='mr-2'
                                            />
                                            <span className='text-sm text-green-600 font-medium'>Save</span>
                                          </label>
                                        </div>
                                      </div>

                                      <div className='flex gap-3 pt-4 border-t'>
                                        <button
                                          type='submit'
                                          disabled={loading}
                                          className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-50'
                                        >
                                          {loading ? (
                                            <div className='h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                                          ) : (
                                            <Check className='h-4 w-4' />
                                          )}
                                          {loading ? 'Updating...' : 'Update Goal'}
                                        </button>
                                        <button
                                          type='button'
                                          onClick={handleCancelEdit}
                                          className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </form>
                                  </div>
                                ) : (
                                  /* Goal Display */
                                  <div 
                                    className='p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors'
                                    onClick={() => handleEditGoal(goal)}
                                  >
                                    <div className='flex justify-between items-start'>
                                      <div className='flex-1'>
                                        <div className='flex items-center gap-2'>
                                          <p className='font-medium text-gray-900'>{goal.title}</p>
                                          <Edit className='h-4 w-4 text-gray-400' />
                                        </div>
                                        {goal.situationDescription && (
                                          <p className='text-sm text-gray-600 mt-1'>{goal.situationDescription}</p>
                                        )}
                                        {goal.shotType && (
                                          <p className='text-xs text-gray-500 mt-1'>{goal.shotType}</p>
                                        )}
                                      </div>
                                      <div className='flex flex-col items-end gap-2'>
                                        <span className={`text-sm font-medium ${
                                          goal.result === 'goal' ? 'text-red-600' : 'text-green-600'
                                        }`}>
                                          {goal.result === 'goal' ? 'Goal' : 'Save'}
                                        </span>
                                        <button
                                          onClick={(e) => handleDeleteGoalClick(goal, e)}
                                          className='text-gray-400 hover:text-red-600 transition-colors p-1 rounded-md hover:bg-red-50'
                                          title='Delete goal'
                                        >
                                          <Trash2 className='h-4 w-4' />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className='mt-4 flex gap-3'>
                        <button
                          onClick={() => {
                            setShowGameDetails(false);
                            setShowGoalForm(true);
                          }}
                          className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600'
                        >
                          <Plus className='h-4 w-4' />
                          Add Goal
                        </button>
                        <button
                          onClick={() => {
                            setShowGameDetails(false);
                            setGameGoals([]);
                          }}
                          className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500'
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && gameToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4'>
            <div className='flex items-center mb-4'>
              <div className='flex-shrink-0'>
                <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center'>
                  <Trash2 className='w-6 h-6 text-red-600' />
                </div>
              </div>
              <div className='ml-4'>
                <h3 className='text-lg font-medium text-gray-900'>
                  Delete Game
                </h3>
                <p className='text-sm text-gray-500'>
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className='mb-6'>
              <p className='text-sm text-gray-700'>
                Are you sure you want to delete the game <strong>vs {gameToDelete.opponent}</strong> 
                ({new Date(gameToDelete.date).toLocaleDateString()})?
              </p>
              <p className='text-sm text-red-600 mt-2 font-medium'>
                This will also delete all associated goals for this game.
              </p>
            </div>
            
            <div className='flex justify-end space-x-3'>
              <button
                onClick={handleCancelDelete}
                disabled={loading}
                className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGame}
                disabled={loading}
                className='px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center gap-2'
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className='w-4 h-4' />
                    Delete Game
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goal Delete Confirmation Modal */}
      {showGoalDeleteConfirmation && goalToDelete && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4'>
            <div className='flex items-center mb-4'>
              <div className='flex-shrink-0'>
                <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center'>
                  <Trash2 className='w-6 h-6 text-red-600' />
                </div>
              </div>
              <div className='ml-4'>
                <h3 className='text-lg font-medium text-gray-900'>
                  Delete Goal
                </h3>
                <p className='text-sm text-gray-500'>
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className='mb-6'>
              <p className='text-sm text-gray-700'>
                Are you sure you want to delete the goal <strong>"{goalToDelete.title}"</strong>?
              </p>
              {goalToDelete.situationDescription && (
                <p className='text-sm text-gray-500 mt-2'>
                  {goalToDelete.situationDescription}
                </p>
              )}
            </div>
            
            <div className='flex justify-end space-x-3'>
              <button
                onClick={handleCancelGoalDelete}
                disabled={loading}
                className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGoal}
                disabled={loading}
                className='px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 flex items-center gap-2'
              >
                {loading ? (
                  <>
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white'></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className='w-4 h-4' />
                    Delete Goal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
